import React, { useEffect, useState } from 'react';

interface FloatingElementProps {
  children?: React.ReactNode;
  className?: string;
  duration?: number; // in seconds
  delay?: number; // in seconds
  translateX?: string; // CSS value
  translateY?: string; // CSS value
  scale?: string; // CSS value 
  rotate?: string; // CSS value in deg
  opacity?: string; // CSS value
  id?: string;
}

const FloatingElement: React.FC<FloatingElementProps> = ({
  children,
  className = '',
  duration = 6,
  delay = 0,
  translateX = '0px',
  translateY = '20px',
  scale = '1.05',
  rotate = '0deg',
  opacity = '0.8',
  id = `float-${Math.random().toString(36).substr(2, 9)}`,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100); // Small delay to ensure animation works
    
    return () => clearTimeout(timer);
  }, []);

  // Create the keyframes style
  const keyframesStyle = `
    @keyframes float-${id} {
      0% {
        transform: translate(0, 0) scale(1) rotate(0deg);
      }
      50% {
        transform: translate(${translateX}, ${translateY}) scale(${scale}) rotate(${rotate});
      }
      100% {
        transform: translate(0, 0) scale(1) rotate(0deg);
      }
    }
  `;

  // Add the style to the document head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = keyframesStyle;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [keyframesStyle]);

  return (
    <div
      className={`transition-all ease-in-out ${className}`}
      style={{
        animation: `float-${id} ${duration}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        opacity: isVisible ? opacity : '0',
        transform: `translate(0, 0) scale(1) rotate(0deg)`,
      }}
    >
      {children || (
        <div className="rounded-full bg-indigo-50 opacity-30 w-24 h-24" />
      )}
    </div>
  );
};

export default FloatingElement; 